import { useEffect, useRef } from 'react'

type Color = [number, number, number]

const vertexSource = `attribute vec2 position;
void main() { gl_Position = vec4(position, 0.0, 1.0); }`

// The approved Liquid Navy study, recolored from the workspace appearance.
const fragmentSource = `precision highp float;
uniform vec2 resolution;
uniform float time;
uniform vec2 pointer;
uniform vec3 shadow;
uniform vec3 body;
uniform vec3 sheen;
uniform float lightAppearance;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p), u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float amplitude = 0.5, value = 0.0;
  mat2 rotation = mat2(0.8, -0.6, 0.6, 0.8);
  for (int i = 0; i < 4; i++) {
    value += amplitude * noise(p);
    p = rotation * p * 2.03 + vec2(2.1, 1.7);
    amplitude *= 0.5;
  }
  return value;
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * resolution) / resolution.y;
  vec2 q = p * 0.95 + (pointer - 0.5) * 0.09;
  vec2 flow = vec2(fbm(q + vec2(time * 0.04, 0.0)), fbm(q + vec2(3.1, -time * 0.035)));
  float field = fbm(q * 1.7 + flow * 3.1 + vec2(time * 0.025, time * 0.03));
  float ribbons = sin(field * 9.0 + p.x * 0.7 + time * 0.08) * 0.5 + 0.5;
  float depth = smoothstep(0.22, 0.8, field);
  vec3 color = mix(shadow, body, depth);
  color = mix(color, sheen, pow(ribbons, 8.0) * depth * 0.52);
  color = mix(color, sheen, pow(smoothstep(0.6, 0.82, field), 2.0) * 0.35);
  float vignette = smoothstep(0.3, 1.0, length(p * vec2(0.6, 1.0)));
  color = mix(color, shadow, vignette * 0.22);
  color = mix(color, shadow, exp(-dot(p, p) * 10.0) * 0.12);
  color += (hash(gl_FragCoord.xy) - 0.5) * mix(0.007, 0.003, lightAppearance);
  gl_FragColor = vec4(color, 1.0);
}`

function colorToken(value: string, fallback: Color): Color {
  const hex = value.trim().match(/^#([\da-f]{3}|[\da-f]{6})$/i)?.[1]
  if (hex) {
    const full = hex.length === 3 ? [...hex].map(char => char + char).join('') : hex
    return [0, 2, 4].map(index => parseInt(full.slice(index, index + 2), 16) / 255) as Color
  }
  const rgb = value.trim().match(/^rgba?\(([^)]+)\)$/)?.[1].split(/[\s,\/]+/).slice(0, 3).map(Number)
  return rgb?.length === 3 && rgb.every(Number.isFinite) ? rgb.map(channel => channel / 255) as Color : fallback
}

function mixColor(a: Color, b: Color, amount: number): Color {
  return a.map((channel, index) => channel + (b[index] - channel) * amount) as Color
}

function createPipeline(gl: WebGLRenderingContext) {
  const shaders: WebGLShader[] = []
  const program = gl.createProgram()
  const buffer = gl.createBuffer()
  if (!program || !buffer) {
    if (program) gl.deleteProgram(program)
    if (buffer) gl.deleteBuffer(buffer)
    return null
  }
  try {
    for (const [type, source] of [[gl.VERTEX_SHADER, vertexSource], [gl.FRAGMENT_SHADER, fragmentSource]] as const) {
      const shader = gl.createShader(type)
      if (!shader) throw new Error('Shader unavailable')
      shaders.push(shader)
      gl.shaderSource(shader, source)
      gl.compileShader(shader)
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error('Shader compilation unavailable')
      gl.attachShader(program, shader)
    }
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('Shader linking unavailable')
    gl.useProgram(program)
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW)
    const position = gl.getAttribLocation(program, 'position')
    gl.enableVertexAttribArray(position)
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
    const uniforms = Object.fromEntries(['resolution', 'time', 'pointer', 'shadow', 'body', 'sheen', 'lightAppearance']
      .map(name => [name, gl.getUniformLocation(program, name)]))
    return { uniforms, dispose: () => { gl.deleteBuffer(buffer); gl.deleteProgram(program) } }
  } catch {
    gl.deleteBuffer(buffer)
    gl.deleteProgram(program)
    return null
  } finally {
    shaders.forEach(shader => gl.deleteShader(shader))
  }
}

/** Decorative liquid field; source controls remain ordinary accessible buttons. */
export function HomeStartScene({ active }: { active: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const activeRef = useRef(active)
  activeRef.current = active
  const syncRef = useRef<() => void>(() => {})

  useEffect(() => {
    const element = canvas.current
    const parent = element?.parentElement
    if (!element || !parent) return
    const gl = element.getContext('webgl', { alpha: false, antialias: false, powerPreference: 'low-power' })
    if (!gl) return
    let pipeline = createPipeline(gl)
    if (!pipeline) return
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    let frame = 0
    let elapsed = 14
    let previous = 0
    let lastDraw = 0
    let currentPointer = [.5, .5]
    let targetPointer = [.5, .5]
    let shadow: Color = [.02, .02, .02]
    let body: Color = [.2, .2, .2]
    let sheen: Color = [.4, .4, .4]
    let light = false

    function readPalette() {
      const styles = getComputedStyle(element!)
      const base = colorToken(styles.getPropertyValue('--canvas'), [.02, .02, .02])
      const accent = colorToken(styles.getPropertyValue('--accent'), [.93, .93, .93])
      light = base[0] * .2126 + base[1] * .7152 + base[2] * .0722 > .5
      shadow = light ? base : mixColor(base, [0, 0, 0], .4)
      body = mixColor(base, accent, light ? .13 : .27)
      sheen = mixColor(base, accent, light ? .25 : .46)
    }
    function draw() {
      if (!pipeline || !element!.width || !element!.height || gl!.isContextLost()) return
      const uniforms = pipeline.uniforms
      gl!.uniform2f(uniforms.resolution, element!.width, element!.height)
      gl!.uniform1f(uniforms.time, elapsed)
      gl!.uniform2f(uniforms.pointer, currentPointer[0], currentPointer[1])
      gl!.uniform3fv(uniforms.shadow, shadow)
      gl!.uniform3fv(uniforms.body, body)
      gl!.uniform3fv(uniforms.sheen, sheen)
      gl!.uniform1f(uniforms.lightAppearance, light ? 1 : 0)
      gl!.drawArrays(gl!.TRIANGLES, 0, 6)
      element!.dataset.ready = 'true'
    }
    function resize() {
      const bounds = element!.getBoundingClientRect()
      const ratio = Math.min(window.devicePixelRatio || 1, 1.25)
      const width = Math.round(bounds.width * ratio)
      const height = Math.round(bounds.height * ratio)
      if (element!.width !== width || element!.height !== height) {
        element!.width = width
        element!.height = height
        gl!.viewport(0, 0, width, height)
      }
      draw()
    }
    function running() { return activeRef.current && !media.matches && !document.hidden && !!pipeline }
    function tick(now: number) {
      frame = 0
      if (!running()) return
      const delta = previous ? Math.min((now - previous) / 1000, .1) : 0
      previous = now
      elapsed += delta
      const easing = 1 - Math.exp(-delta * 1.1)
      currentPointer = currentPointer.map((value, index) => value + (targetPointer[index] - value) * easing)
      if (now - lastDraw >= 1000 / 30) { draw(); lastDraw = now }
      frame = requestAnimationFrame(tick)
    }
    function sync() {
      cancelAnimationFrame(frame)
      frame = 0
      previous = 0
      if (media.matches) { currentPointer = [.5, .5]; targetPointer = [.5, .5] }
      draw()
      if (running()) frame = requestAnimationFrame(tick)
    }
    function themeChanged() { readPalette(); draw() }
    function pointerMoved(event: PointerEvent) {
      if (!running()) return
      const bounds = parent!.getBoundingClientRect()
      targetPointer = [(event.clientX - bounds.left) / bounds.width, 1 - (event.clientY - bounds.top) / bounds.height]
    }
    function pointerLeft() { targetPointer = [.5, .5] }
    function contextLost(event: Event) {
      event.preventDefault()
      cancelAnimationFrame(frame)
      frame = 0
      pipeline = null
      delete element!.dataset.ready
    }
    function contextRestored() {
      pipeline = createPipeline(gl!)
      readPalette()
      gl!.viewport(0, 0, element!.width, element!.height)
      resize()
      sync()
    }
    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(element)
    const themeObserver = new MutationObserver(themeChanged)
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    media.addEventListener('change', sync)
    document.addEventListener('visibilitychange', sync)
    parent.addEventListener('pointermove', pointerMoved)
    parent.addEventListener('pointerleave', pointerLeft)
    element.addEventListener('webglcontextlost', contextLost)
    element.addEventListener('webglcontextrestored', contextRestored)
    syncRef.current = sync
    readPalette()
    resize()
    sync()
    return () => {
      syncRef.current = () => {}
      cancelAnimationFrame(frame)
      resizeObserver.disconnect()
      themeObserver.disconnect()
      media.removeEventListener('change', sync)
      document.removeEventListener('visibilitychange', sync)
      parent.removeEventListener('pointermove', pointerMoved)
      parent.removeEventListener('pointerleave', pointerLeft)
      element.removeEventListener('webglcontextlost', contextLost)
      element.removeEventListener('webglcontextrestored', contextRestored)
      pipeline?.dispose()
      delete element.dataset.ready
    }
  }, [])

  useEffect(() => { syncRef.current() }, [active])
  return <canvas ref={canvas} className="home-start-scene" aria-hidden="true" />
}
